import _compact from "lodash/compact";
import _isString from "lodash/isString";
import _isEmpty from "lodash/isEmpty";
import _map from "lodash/map";

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};
    var ownKeys = Object.keys(source);

    if (typeof Object.getOwnPropertySymbols === 'function') {
      ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) {
        return Object.getOwnPropertyDescriptor(source, sym).enumerable;
      }));
    }

    ownKeys.forEach(function (key) {
      _defineProperty(target, key, source[key]);
    });
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _nonIterableSpread();
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance");
}

function _iterableToArray(iter) {
  if (Symbol.iterator in Object(iter) || Object.prototype.toString.call(iter) === "[object Arguments]") return Array.from(iter);
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) {
    for (var i = 0, arr2 = new Array(arr.length); i < arr.length; i++) {
      arr2[i] = arr[i];
    }

    return arr2;
  }
}

import moment from 'moment-timezone';

var isDateTypeFilter = function isDateTypeFilter(filter) {
  return ['date', 'datetime'].includes(filter.type);
};

var parseDateTypeFilterValue = function parseDateTypeFilterValue(value, dateFormat, tz) {
  // show warning if there is no dateFormat
  // date parsing may be wrong
  var args = [value, dateFormat, tz];

  if (tz && !dateFormat) {
    // eslint-disable-next-line
    console.error('`tz` is defined but `dateFormat` is undefined. This can lead to wrong date parsing result');
    args = [value, tz];
  }

  return moment.tz.apply(moment, _toConsumableArray(args));
};

export var createFilterFragment = function createFilterFragment(_ref) {
  var id = _ref.id,
      key = _ref.key,
      operator = _ref.operator,
      value = _ref.value;
  value = encodeURIComponent(value);
  return {
    id: id,
    value: "filter[param][".concat(id, "][").concat(key, "][").concat(operator, "]=").concat(value)
  };
};
export var createBinding = function createBinding(values, symbol) {
  var binding = values.join(symbol);

  if (values.length > 1) {
    return "(".concat(binding, ")");
  }

  return binding;
};

var combination = function combination(children, symbol) {
  var filters = [];
  children.forEach(function (child) {
    filters.push.apply(filters, _toConsumableArray(child.filters));
  });
  return {
    filters: filters,
    binding: createBinding(children.map(function (v) {
      return v.binding;
    }), symbol)
  };
};

var handlers = {
  and: function and(children) {
    return combination(children, '&');
  },
  or: function or(children) {
    return combination(children, '|');
  },
  between: function between(filter, generateId) {
    var _ref2 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
        tz = _ref2.tz,
        dateFormat = _ref2.dateFormat;

    var isDateType = isDateTypeFilter(filter);
    var finalDateFormat = filter.dateFormat || dateFormat;
    var leftValue = isDateType ? parseDateTypeFilterValue(filter.values[0], finalDateFormat, tz).toISOString() : filter.values[0];
    var rightValue = isDateType ? parseDateTypeFilterValue(filter.values[1], finalDateFormat, tz).toISOString() : filter.values[1];
    var id1 = generateId();
    var id2 = generateId();
    var left = createFilterFragment({
      id: id1,
      key: filter.key,
      operator: 'gte',
      value: leftValue
    });
    var right = createFilterFragment({
      id: id2,
      key: filter.key,
      operator: 'lt',
      value: rightValue
    });
    return {
      binding: "".concat(id1, "&").concat(id2),
      filters: [left.value, right.value]
    };
  },
  baseNot: function baseNot(filter, generateId, operator) {
    var fragments = filter.values.map(function (value) {
      if (value === null) {
        return createFilterFragment({
          id: generateId(),
          key: filter.key,
          operator: 'is_null',
          value: 'true' // Negated in binding to stay consistent with eq/neq behavior

        });
      }

      return createFilterFragment({
        id: generateId(),
        key: filter.key,
        operator: operator,
        value: value
      });
    });
    return {
      binding: createBinding(fragments.map(function (fragment) {
        return "~".concat(fragment.id);
      }), '&'),
      filters: fragments.map(function (fragment) {
        return fragment.value;
      })
    };
  },
  baseFilter: function baseFilter(filter, generateId) {
    var fragments = filter.values.map(function (value) {
      if (value === null) {
        return createFilterFragment({
          id: generateId(),
          key: filter.key,
          operator: 'is_null',
          value: 'true'
        });
      }

      return createFilterFragment({
        id: generateId(),
        key: filter.key,
        operator: filter.operator,
        value: value
      });
    });
    return {
      binding: createBinding(fragments.map(function (fragment) {
        return fragment.id;
      }), '|'),
      filters: fragments.map(function (fragment) {
        return fragment.value;
      })
    };
  },
  eq: function eq(filter, generateId, options) {
    if (isDateTypeFilter(filter) && _compact(filter.values).length) {
      return handlers.dateFilter(filter, generateId, options);
    }

    return handlers.baseFilter(filter, generateId);
  },
  neq: function neq(filter, generateId) {
    return handlers.baseNot(filter, generateId, 'eq'); // neq === !eq
  },
  dateFilter: function dateFilter(filter, generateId) {
    var _ref3 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
        tz = _ref3.tz,
        dateFormat = _ref3.dateFormat; // in ver 3.6.1 was added ability to pass `dateFormat` inside filter object.
    // we need it since `dateFormat` is important when we are working with `datetime/date` filters.
    // Approach when we pass one general dateFormat inside `options` does not make sense
    // since several `datetime` type filters with different date formats in one datatable are possible
    // i think we have to set this approach as default one - we have to pass dateFormat inside filter object. @andrewmuntian
    // @TODO: remove `dateFormat` from `options` object in next major release @andrewmuntian


    var finalDateFormat = filter.dateFormat || dateFormat;
    var date = parseDateTypeFilterValue(filter.values[0], finalDateFormat, tz); // only `date` or `datetime` values are possible here

    var delimiter = filter.type === 'datetime' ? 'minute' : 'day';

    if (filter.operator === 'eq') {
      var values = [date.format(finalDateFormat), date.add(1, delimiter).format(finalDateFormat)];

      var nextFilter = _objectSpread({}, filter, {
        values: values
      });

      return handlers.between(nextFilter, generateId, {
        tz: tz,
        dateFormat: finalDateFormat
      });
    } else if (filter.operator === 'lt' || filter.operator === 'gte') {
      var _nextFilter = _objectSpread({}, filter, {
        values: [date.startOf(delimiter).toISOString()]
      });

      return handlers.baseFilter(_nextFilter, generateId);
    }
  },
  notRelated: function notRelated(filter, generateId) {
    var id1 = generateId();
    var id2 = generateId();
    var parentParam = createFilterFragment({
      id: id1,
      key: filter.key,
      operator: 'any',
      value: id2
    });
    var idParam = createFilterFragment({
      id: id2,
      key: 'id',
      operator: 'eq',
      value: filter.values[0]
    });
    return {
      binding: "~".concat(id1),
      filters: [parentParam.value, idParam.value]
    };
  },
  lt: function lt() {
    var result = handlers.eq.apply(handlers, arguments);

    if (result.filters.length > 1) {
      throw new Error('Only 1 value allowed for lt filter');
    }

    return result;
  },
  lte: function lte() {
    var result = handlers.eq.apply(handlers, arguments);

    if (result.filters.length > 1) {
      throw new Error('Only 1 value allowed for lte filter');
    }

    return result;
  },
  gt: function gt() {
    var result = handlers.eq.apply(handlers, arguments);

    if (result.filters.length > 1) {
      throw new Error('Only 1 value allowed for gt filter');
    }

    return result;
  },
  gte: function gte() {
    var result = handlers.eq.apply(handlers, arguments);

    if (result.filters.length > 1) {
      throw new Error('Only 1 value allowed for gte filter');
    }

    return result;
  },
  notContains: function notContains(filter, generateId) {
    return handlers.baseNot(filter, generateId, 'contains');
  },
  startsWith: function startsWith(filter, generateId) {
    var values = filter.values.map(function (v) {
      return "".concat(v, "%");
    });

    var nextFilter = _objectSpread({}, filter, {
      operator: 'like',
      values: values
    });

    return handlers.like(nextFilter, generateId);
  },
  endsWith: function endsWith(filter, generateId) {
    var values = filter.values.map(function (v) {
      return "%".concat(v);
    });

    var nextFilter = _objectSpread({}, filter, {
      operator: 'like',
      values: values
    });

    return handlers.like(nextFilter, generateId);
  },
  like: function like(filter, generateId) {
    return handlers.baseFilter(filter, generateId);
  },

  get has() {
    return this.eq;
  },

  notHas: function notHas(filter, generateId) {
    return handlers.baseNot(filter, generateId, 'has');
  },

  get contains() {
    return this.eq;
  },

  in: function _in(_ref4, generateId) {
    var values = _ref4.values,
        key = _ref4.key,
        operator = _ref4.operator;
    var id = generateId();
    var value = values.join(',');
    return {
      binding: id,
      filters: [createFilterFragment({
        id: id,
        key: key,
        operator: operator,
        value: value
      })]
    };
  },

  get nin() {
    return this.in;
  }

};
export var parseFilters = function parseFilters(obj, options) {
  if (_isEmpty(obj)) {
    return {
      filters: [],
      binding: ''
    };
  } // Extend filters for custom behavior


  var nodeHandlers = handlers;

  if (options && options.handlers) {
    nodeHandlers = _objectSpread({}, nodeHandlers, options.handlers);
  }

  var id = 0;

  var generateId = function generateId() {
    return "p".concat(++id);
  };

  function traverse(node) {
    var handler = nodeHandlers[node.operator];

    if (node.operator === 'or' || node.operator === 'and') {
      var children = node.values.map(traverse);
      return handler(children);
    }

    return handler(node, generateId, options);
  }

  return traverse(obj);
};
export var prepareTableFilters = function prepareTableFilters(filtersObj) {
  // If received top level filter then dont wrap in AND filter
  if (_isString(filtersObj.operator)) {
    return filtersObj;
  }

  return {
    operator: 'and',
    values: _map(filtersObj)
  };
};
export var getTableQuery = function getTableQuery(filtersObj) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var tableFilters = prepareTableFilters(filtersObj);

  var _parseFilters = parseFilters(tableFilters, options),
      binding = _parseFilters.binding,
      filters = _parseFilters.filters;

  if (!binding.length) {
    return '';
  }

  var paramsString = filters.join('&');
  var bindingString = "filter[binding]=".concat(encodeURIComponent(binding));
  return "".concat(paramsString, "&").concat(bindingString);
};